import React from 'react';
import PropTypes from 'prop-types';
import Add from '@material-ui/icons/Add';
import Done from '@material-ui/icons/Done';
import { FormattedMessage, useIntl } from 'react-intl';
import DialogTemplate from 'components/shared/DialogTemplate';
import { Input, Select } from 'components/FormInputs';
import {
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Grid,
  InputAdornment,
  makeStyles,
} from '@material-ui/core';

import useMediaQuery from '@material-ui/core/useMediaQuery';
import { validatePatched } from 'logic/cottage';
import { useFormik } from 'components/shared/Formik';

const styles = makeStyles((theme) => ({
  plus: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  btnWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  },
  subText: {
    color: theme.palette.grey[600],
  },
}));

const EditBase = ({ isLoading, onSubmit, onClose, isNew, cottage }) => {
  const classes = styles();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const intl = useIntl();
  const formik = useFormik({
    initialValues: {
      area: '',
      beds: '',
      extra_beds: '',
      property_designation_type: '',
      rent_out_type: 'ENTIRE_HOME',
      ...cottage,
    },
    enableReinitialize: true,
    validate: validatePatched({ intl, dialog: 'base' }),
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    isSubmitting,
    isValid,
    submitForm,
    handleBlur,
    dirty: formikDirty,
  } = formik;

  const dirty =
    (!cottage && formikDirty) ||
    cottage?.area !== (values.area || null) ||
    cottage?.beds !== (values.beds || null) ||
    cottage?.extra_beds !== (values.extra_beds || null) ||
    cottage?.property_designation_type !== (values.property_designation_type || null) ||
    cottage?.rent_out_type !== (values.rent_out_type || null);

  return (
    <DialogTemplate
      title={
        isNew ? (
          <FormattedMessage id="cottages.createAd" defaultMessage="Create new ad" />
        ) : (
          <>
            <FormattedMessage id="Base information" defaultMessage="Base information" />
            &nbsp;
            <>
              #{cottage?.id}
              {!isSmall && <>{cottage?.title_sv ? <> - "{cottage?.title_sv}"</> : ''}</>}
            </>
          </>
        )
      }
      onSave={isNew ? undefined : submitForm}
      pristine={!dirty}
      onClose={onClose}
      isUpdating={isLoading}
    >
      <form onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2}>
          {cottage?.commission !== false && (
            <>
              <Grid item xs={12}>
                <Select
                  name="property_designation_type"
                  value={values.property_designation_type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors}
                  label={<FormattedMessage id="Property type" defaultMessage="Property type" />}
                  options={[
                    {
                      value: 'HOUSE',
                      label: intl.formatMessage({
                        id: 'property_designation_type.house',
                        defaultMessage: 'House',
                      }),
                    },
                    {
                      value: 'COTTAGE',
                      label: intl.formatMessage({
                        id: 'property_designation_type.cottage',
                        defaultMessage: 'Cottage',
                      }),
                    },
                    {
                      value: 'APARTMENT',
                      label: intl.formatMessage({
                        id: 'property_designation_type.apartment',
                        defaultMessage: 'Apartment',
                      }),
                    },
                  ]}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <RadioGroup
                  row
                  name="rent_out_type"
                  value={values.rent_out_type}
                  defaultValue="ENTIRE_HOME"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors}
                  required
                >
                  <FormControlLabel
                    value="ENTIRE_HOME"
                    control={<Radio color="primary" />}
                    label={intl.formatMessage({
                      id: 'rent_out_type.entire_home',
                      defaultMessage: 'Entire home',
                    })}
                  />
                  <FormControlLabel
                    value="PART_OF_HOME"
                    control={<Radio color="primary" />}
                    label={intl.formatMessage({
                      id: 'rent_out_type.part_of_home',
                      defaultMessage: 'Part of home',
                    })}
                  />
                </RadioGroup>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Input
              name="area"
              value={values.area}
              onChange={handleChange}
              onBlur={handleBlur}
              errors={errors}
              label={<FormattedMessage id="Area" defaultMessage="Area" />}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    m<sup>2</sup>
                  </InputAdornment>
                ),
                inputProps: { min: 1, max: 10000 },
              }}
              type="number"
              required
            />
          </Grid>
          <Grid item xs={5}>
            <Input
              name="beds"
              value={values.beds}
              onChange={handleChange}
              onBlur={handleBlur}
              errors={errors}
              label={<FormattedMessage id="Beds" defaultMessage="Beds" />}
              type="number"
              InputProps={{ inputProps: { min: 0, max: 10000 } }}
              required
            />
          </Grid>
          <Grid item xs={2} className={classes.plus}>
            <Add color="disabled" />
          </Grid>
          <Grid item xs={5}>
            <Input
              name="extra_beds"
              value={values.extra_beds}
              onChange={handleChange}
              onBlur={handleBlur}
              errors={errors}
              label={<FormattedMessage id="Extra beds" defaultMessage="Extra beds" />}
              type="number"
              InputProps={{ inputProps: { min: 0, max: 10000 } }}
            />
          </Grid>
          <Grid item xs={12} className={classes.btnWrap}>
            {isNew && (
              <Button
                variant="contained"
                color="primary"
                disabled={isLoading || !dirty || !isValid || isSubmitting}
                type="submit"
              >
                {isLoading ? <CircularProgress color="inherit" size={24} /> : <Done />}
                <FormattedMessage id="Create ad" defaultMessage="Create ad" />
              </Button>
            )}
          </Grid>
        </Grid>
      </form>
    </DialogTemplate>
  );
};

EditBase.propTypes = {
  isNew: PropTypes.bool,
  isLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  cottage: PropTypes.shape({
    id: PropTypes.number,
    title_sv: PropTypes.string,
    commission: PropTypes.bool,
    area: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    beds: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    extra_beds: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    property_designation_type: PropTypes.string,
    rent_out_type: PropTypes.string,
  }),
};

export default EditBase;
